import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import BackupSummaryContext from '@totem/components/services/backup/deviceBackups/backupSummary/backupSummaryContext';
import { BackupSummary } from '@totem/components/services/backup/deviceBackups/backupSummary/types';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Filter } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';

export const containerConfiguration: FilterField[] = [
  {
    key: 'regionId',
    label: 'Region',
    sortField: '',
    filterField: 'regionId',
    multiple: true,
    values: [],
  },
  {
    key: 'buildingId',
    label: 'Building',
    sortField: '',
    filterField: 'buildingId',
    multiple: true,
    values: [],
  },
  {
    key: 'controlSystemId',
    label: 'Control System',
    sortField: '',
    filterField: 'controlSystemId',
    multiple: true,
    values: [],
  },
];

type InnerProps = {
  regionId?: string[];
  buildingId?: string[];
  controlSystemId?: string[];
  children?: ReactNode;
};

const BackupSummaryContainerInner = ({
  regionId,
  buildingId,
  controlSystemId,
  children,
}: InnerProps) => {
  const { getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
  } = useContext(InputContext);

  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<BackupSummary>(null);
  const [action, setAction] = useState<string>('');
  const fetchAbortController = useRef<AbortController | null>(null);

  useEffect(() => {
    setRefreshData(true);
  }, [
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
    regionId,
    buildingId,
    controlSystemId,
  ]);

  useEffect(() => {
    if (!refreshData) return;

    fetchAbortController.current?.abort();

    const controller = new AbortController();
    fetchAbortController.current = controller;

    setRefreshData(false);

    setIsLoading(true);

    fetch(`${DEVICES_ENDPOINT}/backupSummary`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(buildParameters()),
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((result: BackupSummary) => {
        setData(result);
      })
      .then(() => {
        setIsLoading(false);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') {
          setIsLoading(false);
        }
      });
  }, [refreshData, getApiToken]);

  const handleAction = (newAction: string) => {
    switch (newAction) {
      case 'refresh':
        setRefreshData(true);

        setAction('');
        break;
      default:
        setAction(newAction);
    }
  };

  return (
    <BackupSummaryContext.Provider
      value={{
        loading: isLoading,
        data,
        action,
        onAction: handleAction,
      }}
    >
      <div>{children}</div>
    </BackupSummaryContext.Provider>
  );
};

type Props = {
  regionId?: string[];
  buildingId?: string[];
  controlSystemId?: string[];
  children?: ReactNode;
};

const BackupSummaryContainer = ({
  regionId,
  buildingId,
  controlSystemId,
  children,
}: Props) => {
  const staticFilters: Filter[] = [];

  if (isNotNull(regionId) && regionId.length > 0) {
    staticFilters.push({ field: 'regionId', values: regionId });
  }
  if (isNotNull(buildingId) && buildingId.length > 0) {
    staticFilters.push({ field: 'buildingId', values: buildingId });
  }
  if (isNotNull(controlSystemId) && controlSystemId.length > 0) {
    staticFilters.push({ field: 'controlSystemId', values: controlSystemId });
  }

  return (
    <InputContainer
      filterConfig={containerConfiguration}
      staticFilters={staticFilters.length > 0 ? staticFilters : undefined}
    >
      <BackupSummaryContainerInner
        regionId={regionId}
        buildingId={buildingId}
        controlSystemId={controlSystemId}
      >
        {children}
      </BackupSummaryContainerInner>
    </InputContainer>
  );
};

export default BackupSummaryContainer;
